import { createRouter, createWebHistory } from 'vue-router'
import { applyRouteSeo } from '@/utils/seo'
import { postAuthPath, waitForAuthUser } from '@/utils/authSession'
import { trackPageView } from '@/utils/analytics'

const NOINDEX = 'noindex, nofollow'

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('../views/HomeView.vue'),
    meta: {
      guestOnly: true,
      seo: {
        title: 'Coinproof | Multi exchange crypto chart scanner and shortlist tool',
        description:
          'From chart idea to a board that watches with you. Strategies, private shapes, shortlists, and match alerts to Telegram, Discord, in-app, or your HTTPS webhook. Free refreshes hourly on one exchange. Pro about every 10 minutes. Charts and filters for your own research, not trading signals or financial advice.',
        robots: 'index, follow',
      },
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/RegisterView.vue'),
    meta: {
      guestOnly: true,
      seo: {
        title: 'Coinproof | Register',
        description: 'Create a Coinproof account to scan exchange boards and shortlist setups.',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/sign-in',
    name: 'signIn',
    component: () => import('../views/SignInView.vue'),
    meta: {
      guestOnly: true,
      seo: {
        title: 'Coinproof | Sign In',
        description: 'Sign in to your Coinproof account.',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/email-verification',
    name: 'verify',
    component: () => import('../views/EmailVerificationView.vue'),
    meta: {
      requiresAuth: true,
      seo: {
        title: 'Coinproof | Verify email',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/subscription-expired',
    name: 'expired',
    component: () => import('../views/SubscriptionExpiredView.vue'),
    meta: {
      requiresAuth: true,
      seo: {
        title: 'Coinproof | Subscription ended',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('../views/TermsView.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Terms of Use',
        description:
          'Terms of Use for Coinproof, the multi-exchange crypto chart scanner and shortlist tool. Charts and filters for your own research, not financial advice.',
        robots: 'index, follow',
      },
    },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('../views/PrivacyView.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Privacy Policy',
        description:
          'Privacy Policy for Coinproof. How we handle account, usage, and support data for the crypto chart scanner.',
        robots: 'index, follow',
      },
    },
  },
  {
    path: '/account-deleted',
    name: 'accountDeleted',
    component: () => import('../views/AccountDeletedView.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Account deleted',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/offline',
    name: 'offline',
    component: () => import('../views/OfflineView.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Offline',
        description: 'Coinproof needs an internet connection to load charts and alerts.',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('../views/AdminView.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Admin',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/feedback',
    name: 'feedback',
    component: () => import('../views/FeedbackView.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Feedback',
        description: 'Feedback on what you expected from Coinproof and what you would change.',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/admin/feedback',
    name: 'adminFeedback',
    component: () => import('../views/AdminFeedbackView.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Feedback answers',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/admin/users',
    name: 'adminUsers',
    component: () => import('../views/AdminUsersView.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Users',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/pattern-debug',
    name: 'patternDebug',
    component: () => import('../views/PatternDebugView.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Pattern debug',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/dashboard/:mode',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
    props: (route) => ({
      exchangeId: route.query.exchangeId,
      currentPage: route.query.currentPage,
      cardFreq: route.query.cardFreq,
      sortBy: route.query.sortBy,
      sortDir: route.query.sortDir,
      filterBy: route.query.filterBy,
      filterPatterns: route.query.filterPatterns,
      trend: route.query.trend,
      volumeSpike: route.query.volumeSpike,
      minVolume: route.query.minVolume,
      maxVolume: route.query.maxVolume,
      minVolatility: route.query.minVolatility,
      maxVolatility: route.query.maxVolatility,
      maxRank: route.query.maxRank,
      minMarketCap: route.query.minMarketCap,
      maxMarketCap: route.query.maxMarketCap,
      show: route.query.show,
    }),
    meta: {
      requiresAuth: true,
      seo: {
        title: 'Coinproof | Dashboard',
        robots: NOINDEX,
      },
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'pageNotFound',
    component: () => import('../views/PageNotFound.vue'),
    meta: {
      seo: {
        title: 'Coinproof | Page not found',
        robots: NOINDEX,
      },
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, from, next) => {
  clearDashboardLightTheme(to.path)

  const needsAuth = to.matched.some((record) => record.meta.requiresAuth)
  const guestOnly = to.matched.some((record) => record.meta.guestOnly)

  if (!needsAuth && !guestOnly) {
    next()
    return
  }

  const user = await waitForAuthUser()
  const browserOffline = typeof navigator !== 'undefined' && !navigator.onLine

  if (needsAuth) {
    // A saved session stays on the dashboard through a Wi-Fi blip.
    // The offline banner covers failed chart loads. Only a confirmed
    // missing user goes to sign-in — and only when the browser is online,
    // so a wake-from-sleep does not look like a logout.
    if (user) {
      next()
      return
    }
    if (browserOffline && to.name !== 'offline') {
      next({ name: 'offline', query: { return: to.fullPath } })
      return
    }
    if (browserOffline) {
      next()
      return
    }
    next({ path: '/sign-in', query: { return: to.fullPath } })
    return
  }

  // Already signed in → skip marketing / auth forms.
  if (guestOnly && user) {
    next(postAuthPath(user) || '/dashboard/analytics')
    return
  }

  next()
})

/**
 * Light dashboard theme toggles `body.dashboard-page` (see DashboardHeader).
 * That class sets a light page background while landing/auth pages assume
 * the default dark body. Strip it whenever we are not on /dashboard.
 */
function clearDashboardLightTheme(toPath) {
  if (typeof document === 'undefined') return
  if (!toPath.startsWith('/dashboard')) {
    document.body.classList.remove('dashboard-page')
  }
}

router.afterEach((to) => {
  clearDashboardLightTheme(to.path)
  applyRouteSeo(to)
  const title = (to.meta && to.meta.seo && to.meta.seo.title) || document.title
  trackPageView(to.fullPath, title)
})

export default router
